'use client';

import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

export * from './get-user-consent';
export interface CookieConsentProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  acceptText?: React.ReactNode;
  declineText?: React.ReactNode;
  doneText?: React.ReactNode;
  strictlyNecessaryText?: React.ReactNode;
  functionalText?: React.ReactNode;
  analyticsText?: React.ReactNode;
  marketingText?: React.ReactNode;
  simplerChoicesText?: React.ReactNode;
  className?: string;
  cookieName?: string;
  domain?: string;
  onAccept?: () => void;
  onDecline?: () => void;
}

export const CookieConsent = ({
  title,
  description = 'We use cookies to improve your experience on our site. By using our site you consent to cookies.',
  acceptText = 'Accept all',
  declineText = 'More options',
  doneText = 'Done',
  strictlyNecessaryText = 'Strictly Necessary',
  functionalText = 'Functional',
  analyticsText = 'Analytics',
  marketingText = 'Marketing',
  simplerChoicesText = '« Simpler choices',
  className,
  cookieName = 'FLOWUS_COOKIE_CONSENT_ACCEPTED',
  domain = '.buildin.ai',
  onAccept,
  onDecline,
}: CookieConsentProps) => {
  const [visible, setVisible] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  // 选项的勾选状态
  const [preferences, setPreferences] = useState({
    strictlyNecessary: true, // 强制选中，不可取消
    functional: true,
    analytics: true,
    marketing: true,
  });

  useEffect(() => {
    // 组件挂载时检查 Cookie 是否已存在
    const match = document.cookie.match(new RegExp('(^| )' + cookieName + '=([^;]+)'));
    if (!match) {
      setVisible(true);
    }
  }, [cookieName]);

  const setConsentCookie = (value: string) => {
    const maxAge = 60 * 60 * 24 * 365;
    let cookieStr = `${cookieName}=${value}; path=/; max-age=${maxAge}`;

    // 如果不是本地测试环境，且传入了 domain，则指定 domain 以便跨子域共享
    if (
      domain &&
      typeof window !== 'undefined' &&
      !['localhost', '127.0.0.1'].includes(window.location.hostname)
    ) {
      cookieStr += `; domain=${domain}`;
    }
    document.cookie = cookieStr;
  };

  const handleAcceptAll = () => {
    // 接受全部选项
    setConsentCookie('true');
    setVisible(false);
    onAccept?.();
  };

  const handleSavePreferences = () => {
    // 保存偏好设置（序列化为 JSON 字符串存储以便后续读取，或者简单存为 false 代表非全量授权）
    // 为了兼容原逻辑，这里将具体偏好序列化，但依然起到阻止再次弹窗的作用
    setConsentCookie(JSON.stringify(preferences));
    setVisible(false);
    // 无论是全接受还是部分拒绝，都可以视为经过了设定
    onDecline?.();
  };

  if (!visible) {
    return null;
  }

  return (
    <div
      data-html2canvas-ignore="true"
      className={classNames(
        'fixed bottom-0 left-0 right-0 z-[1000] p-4 border-t border-gray-200 dark:border-gray-800 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] w-full',
        className
      )}
      style={{ backgroundColor: '#ffffff' }}
    >
      <div className="max-w-7xl mx-auto flex flex-col w-full">
        {/* 精简视图 */}
        {!showOptions ? (
          <div
            className="flex flex-row sm:flex-col items-center sm:items-start justify-between gap-6 max-w-[1280px] mx-auto w-full px-8 sm:px-4"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
              maxWidth: 1280,
              margin: '0 auto',
              width: '100%',
              padding: '0 32px',
            }}
          >
            <div className="text-[13px] text-gray-800 dark:text-gray-200 text-left flex-1 max-w-4xl">
              {title && (
                <strong className="mr-2 font-semibold text-[14px] text-black dark:text-white inline">
                  {title}
                </strong>
              )}
              <span className="leading-relaxed opacity-90">{description}</span>
            </div>

            <div className="flex flex-row justify-end gap-3 shrink-0 sm:self-end">
              <button
                onClick={() => setShowOptions(true)}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  height: 42,
                  padding: '0 20px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  fontSize: 13,
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                className="hover:bg-gray-50 dark:hover:bg-gray-800 dark:text-gray-300 dark:border-gray-700 transition-colors shadow-sm flex-shrink-0"
              >
                {declineText}
              </button>
              <button
                onClick={handleAcceptAll}
                style={{
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  height: 42,
                  padding: '0 24px',
                  borderRadius: 8,
                  border: 'none',
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                className="hover:bg-gray-800 dark:hover:bg-gray-200 dark:bg-white dark:text-black transition-colors shadow-sm flex-shrink-0"
              >
                {acceptText}
              </button>
            </div>
          </div>
        ) : (
          /* 展开更多选项时的详细视图 */
          <div
            className="flex flex-col w-full max-w-[1280px] mx-auto px-8 sm:px-4"
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: 1280,
              margin: '0 auto',
              padding: '0 32px',
              boxSizing: 'border-box',
            }}
          >
            <div
              className="flex flex-row sm:flex-col justify-between items-center sm:items-start mb-4 gap-6"
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 24,
                marginBottom: 16,
                width: '100%',
              }}
            >
              <div
                className="text-[13px] text-gray-800 dark:text-gray-200 text-left flex-1 max-w-4xl"
                style={{ flex: 1, minWidth: 0 }}
              >
                {title && (
                  <strong className="mr-2 font-semibold text-[14px] text-black dark:text-white inline">
                    {title}
                  </strong>
                )}
                <span className="leading-relaxed opacity-90 inline">{description}</span>
              </div>
              <button
                onClick={handleSavePreferences}
                style={{
                  backgroundColor: '#0066FF',
                  color: '#ffffff',
                  height: 42,
                  padding: '0 24px',
                  borderRadius: 8,
                  border: 'none',
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  width: 'auto',
                }}
                className="hover:bg-[#005CE6] transition-colors shadow-sm"
              >
                {doneText}
              </button>
            </div>

            {/* 选项组 */}
            <div
              className="grid grid-cols-4 lg:grid-cols-2 sm:grid-cols-1 gap-4 w-full"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 16,
                width: '100%',
              }}
            >
              {/* Strictly Necessary */}
              <div className="flex items-center justify-between p-3.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/20">
                <span className="text-[13px] font-medium text-gray-900 dark:text-gray-100">
                  {strictlyNecessaryText}
                </span>
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="w-4 h-4 text-[#0066FF] rounded bg-gray-200 border-gray-300 focus:ring-[#0066FF] opacity-40 cursor-not-allowed"
                />
              </div>

              {/* Functional */}
              <div
                className="flex items-center justify-between p-3.5 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-colors cursor-pointer bg-white dark:bg-[#191919]"
                onClick={() =>
                  setPreferences((prev) => ({ ...prev, functional: !prev.functional }))
                }
              >
                <span className="text-[13px] font-medium text-gray-900 dark:text-gray-100 select-none">
                  {functionalText}
                </span>
                <input
                  type="checkbox"
                  checked={preferences.functional}
                  readOnly
                  className="w-4 h-4 text-[#0066FF] rounded border-gray-300 focus:ring-[#0066FF] pointer-events-none"
                />
              </div>

              {/* Analytics */}
              <div
                className="flex items-center justify-between p-3.5 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-colors cursor-pointer bg-white dark:bg-[#191919]"
                onClick={() => setPreferences((prev) => ({ ...prev, analytics: !prev.analytics }))}
              >
                <span className="text-[13px] font-medium text-gray-900 dark:text-gray-100 select-none">
                  {analyticsText}
                </span>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  readOnly
                  className="w-4 h-4 text-[#0066FF] rounded border-gray-300 focus:ring-[#0066FF] pointer-events-none"
                />
              </div>

              {/* Marketing */}
              <div
                className="flex items-center justify-between p-3.5 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-colors cursor-pointer bg-white dark:bg-[#191919]"
                onClick={() => setPreferences((prev) => ({ ...prev, marketing: !prev.marketing }))}
              >
                <span className="text-[13px] font-medium text-gray-900 dark:text-gray-100 select-none">
                  {marketingText}
                </span>
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  readOnly
                  className="w-4 h-4 text-[#0066FF] rounded border-gray-300 focus:ring-[#0066FF] pointer-events-none"
                />
              </div>
            </div>

            {/* 返回精简版视图按钮 */}
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowOptions(false)}
                className="text-[12px] text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors h-8 flex items-center justify-center mx-auto"
              >
                {simplerChoicesText}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
